import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom"; 
// Using native fetch API and React Router for routing

// Define the base API URL
const API_BASE_URL = "https://warfc.com/api";
const MAX_RETRIES = 3;

// Helper function for exponential backoff delay
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export default function ClubsList() {
  const containerRef = useRef();
  const tooltipRef = useRef();
  const [error, setError] = useState(null);
  const navigate = useNavigate(); 

  useEffect(() => {
    // Initial fetch
    fetchClubs();
    
    // Resize handler
    const handleResize = () => fetchClubs(true);
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // forceRedraw flag is used to prevent continuous loading state change on resize
  async function fetchClubs(forceRedraw = false) {
    if (!forceRedraw) setError(null); 

    let lastError = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            // --- 1. Use native fetch to get club data ---
            const response = await fetch(`${API_BASE_URL}/clubs`);
            
            if (!response.ok) {
                // If it's a server error (5xx), we try again
                if (response.status >= 500) {
                    const errorDetails = await response.json().catch(() => ({}));
                    lastError = `Server error ${response.status}: ${errorDetails.error || 'The API gateway failed to respond.'}`;
                    // Log the attempt and prepare to retry
                    console.warn(`Attempt ${attempt + 1} failed with status ${response.status}. Retrying...`);
                    // Wait with exponential backoff: 2s, 4s, 8s...
                    await sleep(2000 * Math.pow(2, attempt)); 
                    continue; // Go to the next attempt
                }

                // If it's a client error (4xx), do not retry, just throw the error
                const errorDetails = await response.json().catch(() => ({}));
                throw new Error(errorDetails.error || `Failed to fetch clubs list. Status: ${response.status}`);
            }

            // Successful fetch
            const data = await response.json();

            if (!data || !Array.isArray(data) || data.length === 0) {
                if (!forceRedraw) setError("No clubs registered yet.");
                return;
            }

            // --- 2. Pass navigate function to the drawing logic ---
            drawBubbleChart(data, navigate);
            return; // Exit the function on success
            
        } catch (e) {
          // This catches network errors (like DNS failure, offline, or initial 502 before response headers)
          if (attempt < MAX_RETRIES - 1) {
            lastError = e.message;
            console.warn(`Attempt ${attempt + 1} failed with network error: ${e.message}. Retrying...`);
            await sleep(2000 * Math.pow(2, attempt));
            continue;
          }
          // If we are on the last attempt, treat the error as final
          lastError = e.message;
        }
    }

    // If the loop completes, all retries failed
    console.error("All fetch attempts failed. Final error:", lastError);
    if (!forceRedraw) setError(lastError || "Could not load club data after multiple attempts. The server may be temporarily down.");
  }

  function structureData(clubs) {
    return {
      name: "WAR FC Clubs",
      children: clubs.map(club => {
        let rawCount = club.fighter_count;
        let fighterCount = 0;

        // --- Defensive Parsing Logic ---
        if (typeof rawCount === 'number') {
            fighterCount = rawCount;
        } else if (typeof rawCount === 'string') {
            // Use base 10 for parsing string numbers
            fighterCount = parseInt(rawCount, 10);
        }
        
        // Final safety check: ensure it's a non-negative integer, default to 0 if parsing failed
        if (isNaN(fighterCount) || fighterCount < 0) {
            fighterCount = 0;
        }
        // --- End Defensive Parsing Logic ---
        
        // This ensures clubs with 0 fighters show a value of 1 for minimum size (so the bubble is visible), 
        // but clubs with > 0 fighters show their real count.
        const valueForD3Pack = Math.max(1, fighterCount);
        
        return {
          name: club.club_name,
          // Use the size-safe value for D3 pack hierarchy
          value: valueForD3Pack, 
          // Store the actual count for the tooltip
          actualFighterCount: fighterCount, 
          clubId: club.club_id,
          logo_url: club.logo_url
        };
      })
    };
  }

  function drawBubbleChart(clubsData, navigate) {
    const container = containerRef.current;
    const tooltip = tooltipRef.current;
    
    // CRITICAL SAFETY CHECK: Ensure container is available and readable
    if (!container || container.clientWidth === 0) {
      console.warn("D3 Container not ready or has zero width. Skipping draw.");
      return; 
    }

    // Use current width/height for responsiveness
    const width = container.clientWidth;
    const size = Math.min(width, 900); // Max size of the visualization
    container.innerHTML = ""; // Clear previous SVG

    // Max fighters based on the original data, ensuring proper scale
    const maxFighters = d3.max(clubsData, c => parseInt(c.fighter_count) || 0);

    const root = d3.hierarchy(structureData(clubsData))
      .sum(d => d.value) // Use the value which is guaranteed >= 1 for d3.pack
      .sort((a, b) => b.value - a.value);

    const pack = d3.pack().size([size, size]).padding(6);
    const nodes = pack(root).leaves();

    // Setup SVG container
    const svg = d3.select(container)
      .append("svg")
      .attr("width", "100%")
      .attr("height", size)
      .attr("viewBox", `0 0 ${size} ${size}`);

    // GRADIENTS for Visual Appeal
    const defs = svg.append("defs");
    // Use a color scale based on the fighter count (maxFighters)
    const colorScale = d3.scaleSequential(d3.interpolateYlOrRd).domain([0, maxFighters]);

    nodes.forEach((d, i) => {
      const gradient = defs.append("radialGradient")
        .attr("id", `grad-${d.data.clubId}`); 
        
      // Use the actual count for coloring
      const color = colorScale(d.data.actualFighterCount);

      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", d3.color(color).brighter(1));

      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", d3.color(color).darker(1))
        .attr("stop-opacity", 0.9);
    });

    // NODE GROUP
    const node = svg.selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .attr("transform", d => `translate(${d.x},${d.y})`)
      .style("cursor", "pointer")
      // --- ROUTING FIX: Use navigate function for smooth SPA transition ---
      .on("click", (event, d) => navigate(`/clubs/${d.data.clubId}`)); 

    // BUBBLE (Circle)
    node.append("circle")
      .attr("r", 0)
      .attr("fill", d => `url(#grad-${d.data.clubId})`)
      .attr("stroke", "#d4af37") // Gold stroke for emphasis
      .attr("stroke-width", 2)
      .attr("class", "club-bubble") 
      .transition()
      .duration(800)
      .delay((d, i) => i * 50)
      .attr("r", d => d.r);

    // --- Image rendering ---
    node.append("image")
        .attr("href", d => `${API_BASE_URL}/uploads/clubs/${d.data.logo_url || "default-club.png"}`)
        .attr("x", d => -d.r)
        .attr("y", d => -d.r)
        .attr("width", d => d.r * 2)
        .attr("height", d => d.r * 2)
        // Clip the image to the circle boundary (SVG clip-path requires center offset for positioning)
        .style("clip-path", d => `circle(${d.r}px at ${d.r}px ${d.r}px)`)
        .style("pointer-events", "none");
    // --- End Image rendering ---

    // LOGO or TEXT (Text label inside the bubble) - Removed as per request

    // TOOLTIP + HOVER
    node.on("mouseover", (event, d) => {
        d3.select(tooltip)
            .style("opacity", 1)
            // Use d.data.actualFighterCount to display the correct count (0 or higher)
            .html(`<strong>${d.data.name}</strong><br/>Fighters: ${d.data.actualFighterCount}`) 
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 28) + "px");

        // Highlight effect
        d3.select(event.currentTarget).select("circle")
            .transition().duration(100)
            .attr("stroke-width", 4)
            .attr("stroke", "#ffeb3b"); 
    })
    .on("mouseout", (event) => {
        d3.select(tooltip).style("opacity", 0);
        
        // Revert highlight effect
        d3.select(event.currentTarget).select("circle")
            .transition().duration(150)
            .attr("stroke-width", 2)
            .attr("stroke", "#d4af37");
    });


    // FLOAT ANIMATION (Subtle movement for a dynamic look)
    const floatAmplitude = 4;
    const floatSpeed = 0.002;
    d3.timer(elapsed => {
        if (!containerRef.current) return true; 
        
        node.attr("transform", d => {
            const xOffset = Math.sin(elapsed * floatSpeed + d.data.clubId) * floatAmplitude;
            const yOffset = Math.cos(elapsed * floatSpeed + d.data.clubId) * floatAmplitude;
            return `translate(${d.x + xOffset},${d.y + yOffset})`;
        });
    });
  }

  return (
    <div className="p-6 text-white min-h-screen bg-black/45 backdrop-blur-md border border-white/20 rounded-lg">
      <h1 className="text-4xl font-extrabold text-[#d4af37] text-center mb-8 border-b border-gray-700 pb-4">
        WAR FC Official Club Roster
      </h1>

      {error && (
        <div className="max-w-xl mx-auto p-4 bg-red-800 text-white rounded-lg text-center mb-6">
          {/* Display the detailed error message for better user feedback */}
          Error loading data. The server returned: <strong>{error}</strong>
          <p className="mt-2 text-sm">The application automatically tried up to 3 times to fetch the data. Please check the API status or try again later.</p>
        </div>
      )}

      {/* D3 container */}
      <div 
        ref={containerRef} 
        id="clubs-list-container" 
        className="relative mx-auto" 
        style={{ maxWidth: '900px' }}
      >
        {/* Chart will be drawn here */}
      </div>
      
      {/* Club Tooltip - Styled with Tailwind for visibility */}
      <div 
        ref={tooltipRef} 
        id="club-tooltip" 
        style={{ opacity: 0, position: 'absolute', pointerEvents: 'none' }}
        className="px-3 py-1 bg-gray-900 text-[#d4af37] border border-[#d4af37] rounded-md shadow-lg transition duration-200 z-50"
      >
        {/* Tooltip content goes here */}
      </div>

    </div>
  );
}